/**
 * =============================================================================
 * PATTERN RESOLVER UTILITY
 * =============================================================================
 *
 * Handles extraction and normalization of data based on response patterns.
 * Used for both breaching entities and supporting data resolution.
 *
 * Response Patterns:
 * - PAGINATED_LIST (A): { data: T[], total, page, pageSize, hasNext }
 * - AGGREGATE_WITH_GROUPINGS (B): { data: [{ grouping: {...}, data: {...} }] }
 * - ENTITY_WITH_NESTED_GROUPINGS (C): { data: [{ ...entity, groupings: {...} }] }
 * - SINGLE_ENTITY (D): Direct entity object
 * - SCALAR_VALUE (E): Primitive value (not used for drill-down)
 * - TIME_SERIES (F): { series: [{ metric, data: [{label, periodDateKey, value}] }] }
 */

// =============================================================================
// TYPES
// =============================================================================

export enum ResponsePattern {
    PAGINATED_LIST = 'PAGINATED_LIST',
    AGGREGATE_WITH_GROUPINGS = 'AGGREGATE_WITH_GROUPINGS',
    ENTITY_WITH_NESTED_GROUPINGS = 'ENTITY_WITH_NESTED_GROUPINGS',
    SINGLE_ENTITY = 'SINGLE_ENTITY',
    SCALAR_VALUE = 'SCALAR_VALUE',
    TIME_SERIES = 'TIME_SERIES',
}

// TIME_SERIES types (Pattern F)
export interface TimeSeriesDataPoint {
    label: string;
    periodDateKey: string;
    value: number | null;
}

export interface TimeSeriesEntry {
    metric: string;
    data: TimeSeriesDataPoint[];
}

export interface TimeSeriesResponse {
    series: TimeSeriesEntry[];
    period_type?: string;
}

export interface NamedTimeSeries {
    name: string;
    response: TimeSeriesResponse;
}

/** Returns true if value is a TIME_SERIES response (Pattern F). */
export function isTimeSeriesResponse(value: unknown): value is TimeSeriesResponse {
    if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
    const v = value as Record<string, unknown>;
    if (!Array.isArray(v.series) || v.series.length === 0) return false;
    const first = v.series[0] as Record<string, unknown>;
    return (
        typeof first.metric === 'string' &&
        Array.isArray(first.data) &&
        first.data.length > 0 &&
        typeof (first.data[0] as Record<string, unknown>).label === 'string'
    );
}

/** Extract all TIME_SERIES entries from rawSupportingData. */
export function extractTimeSeriesEntries(
    supportingData: Record<string, unknown>,
): NamedTimeSeries[] {
    const results: NamedTimeSeries[] = [];
    for (const [name, value] of Object.entries(supportingData)) {
        if (name.startsWith('_')) continue;
        if (isTimeSeriesResponse(value)) {
            results.push({ name, response: value });
        }
    }
    return results;
}

export enum GroupingTypeKey {
    DATE = 'DATE',
    PRODUCT = 'PRODUCT',
    WAREHOUSE = 'WAREHOUSE',
    CHANNEL = 'CHANNEL',
    VENDOR = 'VENDOR',
}

export interface PatternMeta {
    responsePattern: ResponsePattern;
    primaryKey?: string;
    groupingTypes?: GroupingTypeKey[];
    nestedGroupingsKey?: string;
    nestedDataFields?: string[];
}

export interface ResolvedEntity {
    [key: string]: unknown;
    _primaryKey?: string;
    _primaryValue?: unknown;
    _groupings?: Record<string, unknown>;
}

export interface DrillDownConfig {
    title: string;
    data: Record<string, unknown>[];
    linkingKey?: string;
    hasNestedData: boolean;
    sourceArrayName: string;
}

// =============================================================================
// CONSTANTS
// =============================================================================

const GROUPING_TYPE_FIELD_MAP: Record<GroupingTypeKey, { keyField: string; nameField?: string; extraFields?: string[] }> = {
    [GroupingTypeKey.DATE]: { keyField: 'date', extraFields: ['period_type'] },
    [GroupingTypeKey.PRODUCT]: { keyField: 'product_id', nameField: 'product_name', extraFields: ['sku'] },
    [GroupingTypeKey.WAREHOUSE]: { keyField: 'warehouse_id', nameField: 'warehouse_name' },
    [GroupingTypeKey.CHANNEL]: { keyField: 'channel_id', nameField: 'channel_name' },
    [GroupingTypeKey.VENDOR]: { keyField: 'vendor_id', nameField: 'vendor_name' },
};

const LINKING_KEYS = ['product_id', 'productId', 'sku', 'purchaseOrderId', 'warehouseId', 'warehouse_id', 'vendorId', 'vendor_id', 'channelId', 'channel_id', 'id', 'name'];

// =============================================================================
// BREACHING ENTITIES RESOLUTION
// =============================================================================

/**
 * Normalize breaching entities for display in the pinned alert card.
 * Uses STRUCTURAL detection - does not rely on specific key names.
 * 
 * - Extracts L1 primitives only
 * - Excludes nested objects/arrays (identifier-keyed groupings)
 * - Flattens Pattern B typed grouping structure to L1 fields
 */
export function resolveBreachingEntities(
    entities: unknown[],
    meta?: PatternMeta,
): ResolvedEntity[] {
    if (!entities || !Array.isArray(entities) || entities.length === 0) {
        return [];
    }

    const pattern = meta?.responsePattern ?? detectPattern(entities);

    switch (pattern) {
        case ResponsePattern.AGGREGATE_WITH_GROUPINGS:
            // Pattern B: Flatten typed grouping structure
            return entities.map(entity => flattenPatternBEntity(entity as Record<string, unknown>));
        
        case ResponsePattern.ENTITY_WITH_NESTED_GROUPINGS:
            // Pattern C: Extract L1 primitives, store identifier-keyed groupings separately
            return entities.map(entity => extractL1PrimitivesStructural(entity as Record<string, unknown>, meta?.primaryKey, true));
        
        default:
            // Pattern A: Simple L1 primitive extraction
            return entities.map(entity => extractL1PrimitivesStructural(entity as Record<string, unknown>, meta?.primaryKey, false));
    }
}

/**
 * Extract L1 primitives from entity using STRUCTURAL detection.
 * - Skips arrays
 * - Skips typed grouping objects (Pattern B - has children with 'key' property)
 * - Optionally stores identifier-keyed groupings (Pattern C) for drill-down
 */
function extractL1PrimitivesStructural(
    entity: Record<string, unknown>,
    primaryKey?: string,
    storeIdentifierGroupings?: boolean,
): ResolvedEntity {
    const resolved: ResolvedEntity = {};

    for (const [key, value] of Object.entries(entity)) {
        // Skip arrays
        if (Array.isArray(value)) continue;
        
        // Skip typed grouping objects (Pattern B structure)
        if (isTypedGroupingObject(value)) continue;
        
        // Handle identifier-keyed objects (Pattern C structure)
        if (isIdentifierKeyedObject(value)) {
            if (storeIdentifierGroupings) {
                resolved._groupings = value as Record<string, unknown>;
            }
            continue;
        }
        
        // Skip any other nested objects
        if (typeof value === 'object' && value !== null) continue;
        
        resolved[key] = value;
    }

    // Mark primary key
    const effectivePrimaryKey = primaryKey ?? findPrimaryKey(entity);
    if (effectivePrimaryKey && resolved[effectivePrimaryKey] !== undefined) {
        resolved._primaryKey = effectivePrimaryKey;
        resolved._primaryValue = resolved[effectivePrimaryKey];
    }

    return resolved;
}

/**
 * Flatten Pattern B entity: typed grouping → L1 fields, data object → L1 fields
 * Uses STRUCTURAL detection to find the typed grouping and data objects.
 */
function flattenPatternBEntity(entity: Record<string, unknown>): ResolvedEntity {
    const resolved: ResolvedEntity = {};
    let foundPrimaryKey: string | undefined;
    let foundPrimaryValue: unknown;

    // Find typed grouping object structurally (the one where all children have 'key' property)
    const typedGrouping = findTypedGroupingObject(entity);
    
    // Find data object structurally (object with only primitives, sibling to typed grouping)
    const dataObj = findDataObject(entity);

    // Flatten typed grouping object
    if (typedGrouping) {
        for (const [groupType, groupData] of Object.entries(typedGrouping)) {
            if (!groupData || typeof groupData !== 'object') continue;
            const gd = groupData as Record<string, unknown>;
            const mapping = GROUPING_TYPE_FIELD_MAP[groupType as GroupingTypeKey];
            if (mapping) {
                resolved[mapping.keyField] = gd.key;
                if (mapping.nameField && gd.name) {
                    resolved[mapping.nameField] = gd.name;
                }
                if (mapping.extraFields) {
                    for (const extraField of mapping.extraFields) {
                        const sourceField = extraField === 'period_type' ? 'periodType' : extraField;
                        if (gd[sourceField] !== undefined) {
                            resolved[extraField] = gd[sourceField];
                        }
                    }
                }
                // PRODUCT is primary key preference
                if (groupType === 'PRODUCT' || !foundPrimaryKey) {
                    foundPrimaryKey = mapping.keyField;
                    foundPrimaryValue = gd.key;
                }
            } else {
                // Unknown grouping type - still extract key/name if present
                resolved[groupType.toLowerCase() + '_id'] = gd.key;
                if (gd.name) {
                    resolved[groupType.toLowerCase() + '_name'] = gd.name;
                }
                if (!foundPrimaryKey) {
                    foundPrimaryKey = groupType.toLowerCase() + '_id';
                    foundPrimaryValue = gd.key;
                }
            }
        }
    }

    // Flatten data object (the aggregate values)
    if (dataObj) {
        for (const [key, value] of Object.entries(dataObj)) {
            if (typeof value !== 'object' || value === null) {
                resolved[key] = value;
            }
        }
    }

    // Copy any other L1 primitives (excluding objects we've already processed)
    for (const [key, value] of Object.entries(entity)) {
        if (typeof value !== 'object' || value === null) {
            resolved[key] = value;
        }
    }

    if (foundPrimaryKey) {
        resolved._primaryKey = foundPrimaryKey;
        resolved._primaryValue = foundPrimaryValue;
    }

    return resolved;
}

/**
 * STRUCTURAL: Find the typed grouping object (where all children have 'key' property).
 */
function findTypedGroupingObject(item: Record<string, unknown>): Record<string, Record<string, unknown>> | null {
    for (const value of Object.values(item)) {
        if (!isNonArrayObject(value)) continue;
        
        const objValue = value as Record<string, unknown>;
        const entries = Object.entries(objValue);
        if (entries.length === 0) continue;
        
        const allChildrenHaveKey = entries.every(([, child]) => {
            if (!isNonArrayObject(child)) return false;
            return 'key' in (child as Record<string, unknown>);
        });
        
        if (allChildrenHaveKey) {
            return objValue as Record<string, Record<string, unknown>>;
        }
    }
    return null;
}

/**
 * STRUCTURAL: Find the data object (object with only primitive values).
 */
function findDataObject(item: Record<string, unknown>): Record<string, unknown> | null {
    for (const value of Object.values(item)) {
        if (!isNonArrayObject(value)) continue;
        
        const objValue = value as Record<string, unknown>;
        const entries = Object.entries(objValue);
        if (entries.length === 0) continue;
        
        // Check if ALL values are primitives (this is a data/metrics object)
        const allPrimitives = entries.every(([, v]) => typeof v !== 'object' || v === null);
        
        if (allPrimitives) {
            return objValue;
        }
    }
    return null;
}

// =============================================================================
// SUPPORTING DATA RESOLUTION - MAIN FUNCTION
// =============================================================================

/**
 * Resolve ALL matching data from supportingData for a given key/value.
 * This is the MAIN function for drill-down resolution.
 * 
 * It searches ALL arrays in supportingData and returns drill-down configs for each match.
 * 
 * @param supportingData - The rawSupportingData from alert
 * @param linkingKey - The key to match (e.g., 'product_id')
 * @param linkingValue - The value to match (e.g., 'SKU123')
 * @returns Array of DrillDownConfig ready for display
 */
export function resolveAllMatchingData(
    supportingData: Record<string, unknown>,
    linkingKey: string,
    linkingValue: string,
): DrillDownConfig[] {
    const results: DrillDownConfig[] = [];

    for (const [arrayName, arrayValue] of Object.entries(supportingData)) {
        // Skip metadata keys
        if (arrayName.startsWith('_')) continue;
        // Skip TIME_SERIES responses — rendered as charts, not tables
        if (isTimeSeriesResponse(arrayValue)) continue;

        const data = normalizeToArray(arrayValue);
        if (data.length === 0) continue;

        const firstItem = data[0] as Record<string, unknown>;
        if (typeof firstItem !== 'object' || firstItem === null) continue;

        const pattern = detectPattern(data);
        const matchingItems = findMatchingItemsInArray(data as Record<string, unknown>[], linkingKey, linkingValue, pattern);

        if (matchingItems.length === 0) continue;

        // Extract drill-down data based on pattern
        const drillDowns = extractDrillDownForPattern(matchingItems, pattern, arrayName, linkingValue);
        results.push(...drillDowns);
    }

    return results;
}

/**
 * Find items in array that match the given key/value.
 * Uses STRUCTURAL detection for Pattern B's typed grouping structure.
 */
function findMatchingItemsInArray(
    data: Record<string, unknown>[],
    key: string,
    value: string,
    pattern: ResponsePattern,
): Record<string, unknown>[] {
    return data.filter(item => {
        // Pattern B: Check inside typed grouping structure (structurally detected)
        if (pattern === ResponsePattern.AGGREGATE_WITH_GROUPINGS) {
            const typedGrouping = findTypedGroupingObject(item);
            if (typedGrouping) {
                for (const [groupType, groupData] of Object.entries(typedGrouping)) {
                    if (!groupData) continue;
                    const mapping = GROUPING_TYPE_FIELD_MAP[groupType as GroupingTypeKey];
                    // Match if the requested key maps to this grouping type
                    if (mapping && keysMatch(mapping.keyField, key) && String(groupData.key) === value) {
                        return true;
                    }
                    // Also check if groupType itself matches (for unknown types)
                    if (keysMatch(groupType.toLowerCase() + '_id', key) && String(groupData.key) === value) {
                        return true;
                    }
                }
            }
        }

        // Standard: Check L1 keys with normalization
        const matchedKey = findMatchingKey(item, key);
        if (matchedKey && String(item[matchedKey]) === value) {
            return true;
        }

        return false;
    });
}

/**
 * Extract drill-down configurations based on pattern.
 * Uses STRUCTURAL detection - does not rely on specific key names.
 */
function extractDrillDownForPattern(
    items: Record<string, unknown>[],
    pattern: ResponsePattern,
    arrayName: string,
    contextValue: string,
): DrillDownConfig[] {
    const results: DrillDownConfig[] = [];

    switch (pattern) {
        case ResponsePattern.AGGREGATE_WITH_GROUPINGS:
            // Pattern B: Flatten typed grouping + data into displayable rows
            // The aggregate data IS the content to show
            const flattenedRows = items.map(item => flattenPatternBEntity(item));
            results.push({
                title: formatLabel(arrayName),
                data: flattenedRows,
                linkingKey: findLinkingKeyInData(flattenedRows[0] || {}),
                hasNestedData: false, // Pattern B items are leaf nodes
                sourceArrayName: arrayName,
            });
            break;

        case ResponsePattern.ENTITY_WITH_NESTED_GROUPINGS:
            // Pattern C: First add L1 data, then extract identifier-keyed groupings + nested arrays
            // Flatten items to L1 primitives only for display
            const flattenedPatternCItems = items.map(item => {
                const flattened: Record<string, unknown> = {};
                for (const [k, v] of Object.entries(item)) {
                    // Only include primitives (L1 data)
                    if (v === null || typeof v !== 'object') {
                        flattened[k] = v;
                    }
                }
                return flattened;
            });
            
            // Add L1 data as first drill-down
            // hasNestedData is false because we extract nested content as separate configs below
            if (flattenedPatternCItems.length > 0 && Object.keys(flattenedPatternCItems[0]).length > 0) {
                results.push({
                    title: `${formatLabel(arrayName)} for ${contextValue}`,
                    data: flattenedPatternCItems,
                    linkingKey: findLinkingKeyInData(flattenedPatternCItems[0]),
                    hasNestedData: false, // Nested content is extracted separately
                    sourceArrayName: arrayName,
                });
            }
            
            // Then extract ALL nested content:
            // - Identifier-keyed groupings
            // - Nested arrays
            // - Any object with object children (fallback)
            const seenPatternCKeys = new Set<string>();
            for (const item of items) {
                // Find identifier-keyed groupings structurally
                const identifierGroupings = findIdentifierKeyedGroupingsObject(item);
                if (identifierGroupings && !seenPatternCKeys.has(identifierGroupings.key)) {
                    seenPatternCKeys.add(identifierGroupings.key);
                    const { key: groupingsKey, data: groupingsObj } = identifierGroupings;
                    const groupingsData = expandGroupingsObject(groupingsObj);
                    if (groupingsData.length > 0) {
                        results.push({
                            title: `${formatLabel(groupingsKey)} for ${contextValue}`,
                            data: groupingsData,
                            linkingKey: findLinkingKeyInData(groupingsData[0] || {}),
                            hasNestedData: false,
                            sourceArrayName: `${arrayName}_${groupingsKey}`,
                        });
                    }
                }

                // Extract all other nested content
                for (const [key, value] of Object.entries(item)) {
                    if (seenPatternCKeys.has(key)) continue;
                    
                    // Nested arrays
                    if (Array.isArray(value) && value.length > 0 && typeof value[0] === 'object') {
                        seenPatternCKeys.add(key);
                        results.push({
                            title: `${formatLabel(key)} for ${contextValue}`,
                            data: value as Record<string, unknown>[],
                            linkingKey: findLinkingKeyInData(value[0] as Record<string, unknown>),
                            hasNestedData: hasDeepNesting(value),
                            sourceArrayName: `${arrayName}_${key}`,
                        });
                        continue;
                    }
                    
                    // Identifier-keyed objects (might have been missed by findIdentifierKeyedGroupingsObject)
                    if (isIdentifierKeyedObject(value)) {
                        seenPatternCKeys.add(key);
                        const expandedData = expandGroupingsObject(value as Record<string, Record<string, unknown>>);
                        if (expandedData.length > 0) {
                            results.push({
                                title: `${formatLabel(key)} for ${contextValue}`,
                                data: expandedData,
                                linkingKey: findLinkingKeyInData(expandedData[0] || {}),
                                hasNestedData: false,
                                sourceArrayName: `${arrayName}_${key}`,
                            });
                        }
                        continue;
                    }
                    
                    // Fallback: Any object with object children (expand as table)
                    if (isNonArrayObject(value)) {
                        const objValue = value as Record<string, unknown>;
                        const objectChildren = Object.entries(objValue).filter(
                            ([, v]) => isNonArrayObject(v)
                        );
                        if (objectChildren.length > 0) {
                            seenPatternCKeys.add(key);
                            const expandedData = expandGroupingsObject(
                                objValue as Record<string, Record<string, unknown>>
                            );
                            if (expandedData.length > 0) {
                                results.push({
                                    title: `${formatLabel(key)} for ${contextValue}`,
                                    data: expandedData,
                                    linkingKey: findLinkingKeyInData(expandedData[0] || {}),
                                    hasNestedData: false,
                                    sourceArrayName: `${arrayName}_${key}`,
                                });
                            }
                        }
                    }
                }
            }
            break;

        case ResponsePattern.PAGINATED_LIST:
        default:
            // Pattern A: First add L1 data (matched items), then extract nested content
            // Flatten items to L1 primitives only for display
            const flattenedPatternAItems = items.map(item => {
                const flattened: Record<string, unknown> = {};
                for (const [k, v] of Object.entries(item)) {
                    // Only include primitives (L1 data)
                    if (v === null || typeof v !== 'object') {
                        flattened[k] = v;
                    }
                }
                return flattened;
            });
            
            // Add L1 data as first drill-down (similar to Pattern B)
            // hasNestedData is false because we extract nested content as separate configs below
            if (flattenedPatternAItems.length > 0 && Object.keys(flattenedPatternAItems[0]).length > 0) {
                results.push({
                    title: `${formatLabel(arrayName)} for ${contextValue}`,
                    data: flattenedPatternAItems,
                    linkingKey: findLinkingKeyInData(flattenedPatternAItems[0]),
                    hasNestedData: false, // Nested content is extracted separately
                    sourceArrayName: arrayName,
                });
            }
            
            // Then extract ALL nested content from items:
            // - Nested arrays
            // - Identifier-keyed objects (like groupings)
            // - Any object with object children (fallback for other nested structures)
            const seenNestedKeys = new Set<string>();
            for (const item of items) {
                for (const [key, value] of Object.entries(item)) {
                    if (seenNestedKeys.has(key)) continue;
                    
                    // Nested arrays
                    if (Array.isArray(value) && value.length > 0 && typeof value[0] === 'object') {
                        seenNestedKeys.add(key);
                        results.push({
                            title: `${formatLabel(key)} for ${contextValue}`,
                            data: value as Record<string, unknown>[],
                            linkingKey: findLinkingKeyInData(value[0] as Record<string, unknown>),
                            hasNestedData: hasDeepNesting(value),
                            sourceArrayName: `${arrayName}_${key}`,
                        });
                        continue;
                    }
                    
                    // Identifier-keyed objects (warehouse breakdowns, groupings, etc.)
                    if (isIdentifierKeyedObject(value)) {
                        seenNestedKeys.add(key);
                        const expandedData = expandGroupingsObject(value as Record<string, Record<string, unknown>>);
                        if (expandedData.length > 0) {
                            results.push({
                                title: `${formatLabel(key)} for ${contextValue}`,
                                data: expandedData,
                                linkingKey: findLinkingKeyInData(expandedData[0] || {}),
                                hasNestedData: false,
                                sourceArrayName: `${arrayName}_${key}`,
                            });
                        }
                        continue;
                    }
                    
                    // Fallback: Any object with object children (expand as table)
                    if (isNonArrayObject(value)) {
                        const objValue = value as Record<string, unknown>;
                        const objectChildren = Object.entries(objValue).filter(
                            ([, v]) => isNonArrayObject(v)
                        );
                        if (objectChildren.length > 0) {
                            seenNestedKeys.add(key);
                            // Try to expand as groupings-style object
                            const expandedData = expandGroupingsObject(
                                objValue as Record<string, Record<string, unknown>>
                            );
                            if (expandedData.length > 0) {
                                results.push({
                                    title: `${formatLabel(key)} for ${contextValue}`,
                                    data: expandedData,
                                    linkingKey: findLinkingKeyInData(expandedData[0] || {}),
                                    hasNestedData: false,
                                    sourceArrayName: `${arrayName}_${key}`,
                                });
                            }
                        }
                    }
                }
            }
            break;
    }

    return results;
}

/**
 * STRUCTURAL: Find identifier-keyed groupings object with its key name.
 * More flexible detection that handles various data structures.
 */
function findIdentifierKeyedGroupingsObject(item: Record<string, unknown>): { key: string; data: Record<string, Record<string, unknown>> } | null {
    for (const [key, value] of Object.entries(item)) {
        if (!isNonArrayObject(value)) continue;
        
        const objValue = value as Record<string, unknown>;
        const entries = Object.entries(objValue);
        if (entries.length === 0) continue;
        
        // Check if MOST children are data objects (allow some non-objects for metadata)
        // At least one child must be an object
        const objectChildren = entries.filter(([, child]) => isNonArrayObject(child));
        if (objectChildren.length === 0) continue;
        
        // Most children should be data objects (no 'key' property - not Pattern B typed groupings)
        const dataObjectChildren = objectChildren.filter(([, child]) => {
            return !('key' in (child as Record<string, unknown>));
        });
        
        // At least half of object children should be data objects
        if (dataObjectChildren.length < objectChildren.length / 2) continue;
        
        // Check if keys look like identifiers (contain digits, hyphens, or are not ALL_UPPERCASE)
        const keysAreIdentifiers = entries.some(([k]) => {
            if (/\d/.test(k)) return true;  // Contains a digit
            if (k.includes('-') || k.includes('_')) return true;  // Contains separator
            if (k !== k.toUpperCase()) return true;  // Not all uppercase
            return false;
        });
        
        if (keysAreIdentifiers && dataObjectChildren.length > 0) {
            // Filter to only include object children
            const filteredData: Record<string, Record<string, unknown>> = {};
            for (const [childKey, childValue] of entries) {
                if (isNonArrayObject(childValue)) {
                    filteredData[childKey] = childValue as Record<string, unknown>;
                }
            }
            return { key, data: filteredData };
        }
    }
    return null;
}

/**
 * STRUCTURAL: Check if a value is an identifier-keyed object.
 * More flexible detection that handles various data structures.
 */
function isIdentifierKeyedObject(value: unknown): boolean {
    if (!isNonArrayObject(value)) return false;
    
    const objValue = value as Record<string, unknown>;
    const entries = Object.entries(objValue);
    if (entries.length === 0) return false;
    
    // Check if MOST children are data objects (allow some non-objects for metadata)
    const objectChildren = entries.filter(([, child]) => isNonArrayObject(child));
    if (objectChildren.length === 0) return false;
    
    // Most children should be data objects (no 'key' property - not Pattern B typed groupings)
    const dataObjectChildren = objectChildren.filter(([, child]) => {
        return !('key' in (child as Record<string, unknown>));
    });
    
    // At least half of object children should be data objects
    if (dataObjectChildren.length < objectChildren.length / 2) return false;
    
    // Check if keys look like identifiers (contain digits, hyphens, or are not ALL_UPPERCASE)
    return entries.some(([k]) => {
        if (/\d/.test(k)) return true;  // Contains a digit
        if (k.includes('-') || k.includes('_')) return true;  // Contains separator
        if (k !== k.toUpperCase()) return true;  // Not all uppercase
        return false;
    });
}

// =============================================================================
// DRILL-DOWN CLICK HELPERS
// =============================================================================

/**
 * Extract ALL nested data from an array of items.
 * This is used to auto-expand nested content (groupings, nested arrays) 
 * when first showing drill-down data, so users don't need to click again.
 * 
 * @param items - Array of items to extract nested data from
 * @param contextValue - Value for labeling (e.g., product_id value)
 * @param sourceArrayName - Name of the source array for deduplication
 * @returns Array of DrillDownConfig for all nested data found
 */
export function extractAllNestedDataFromItems(
    items: Record<string, unknown>[],
    contextValue: string,
    sourceArrayName: string,
): DrillDownConfig[] {
    const results: DrillDownConfig[] = [];
    const seenKeys = new Set<string>();

    for (const item of items) {
        for (const [key, value] of Object.entries(item)) {
            // Skip if we've already processed this key (avoid duplicates)
            if (seenKeys.has(key)) continue;

            // Skip typed grouping objects (Pattern B) - these are already flattened
            if (isTypedGroupingObject(value)) continue;

            // Nested arrays
            if (Array.isArray(value) && value.length > 0 && typeof value[0] === 'object') {
                seenKeys.add(key);
                results.push({
                    title: `${formatLabel(key)} for ${contextValue}`,
                    data: value as Record<string, unknown>[],
                    linkingKey: findLinkingKeyInData(value[0] as Record<string, unknown>),
                    hasNestedData: hasDeepNesting(value),
                    sourceArrayName: `${sourceArrayName}_${key}`,
                });
            }

            // Identifier-keyed objects (warehouse breakdowns, groupings, etc.)
            if (isIdentifierKeyedObject(value)) {
                seenKeys.add(key);
                const expandedData = expandGroupingsObject(value as Record<string, Record<string, unknown>>);
                results.push({
                    title: `${formatLabel(key)} for ${contextValue}`,
                    data: expandedData,
                    linkingKey: findLinkingKeyInData(expandedData[0] || {}),
                    hasNestedData: false, // Expanded groupings are typically leaf nodes
                    sourceArrayName: `${sourceArrayName}_${key}`,
                });
            }
        }
    }

    return results;
}

/**
 * Extract nested data from a clicked item for further drill-down.
 * Uses STRUCTURAL detection - does not rely on specific key names.
 */
export function extractNestedDataFromItem(
    item: Record<string, unknown>,
    primaryValue: string,
): DrillDownConfig[] {
    const results: DrillDownConfig[] = [];

    for (const [key, value] of Object.entries(item)) {
        // Skip typed grouping objects (Pattern B) - these are already flattened
        if (isTypedGroupingObject(value)) continue;

        // Nested arrays
        if (Array.isArray(value) && value.length > 0 && typeof value[0] === 'object') {
            results.push({
                title: `${formatLabel(key)} for ${primaryValue}`,
                data: value as Record<string, unknown>[],
                linkingKey: findLinkingKeyInData(value[0] as Record<string, unknown>),
                hasNestedData: hasDeepNesting(value),
                sourceArrayName: key,
            });
        }

        // Identifier-keyed objects (warehouse breakdowns, etc.)
        if (isIdentifierKeyedObject(value)) {
            const expandedData = expandGroupingsObject(value as Record<string, Record<string, unknown>>);
            results.push({
                title: `${formatLabel(key)} for ${primaryValue}`,
                data: expandedData,
                linkingKey: 'warehouse_id',
                hasNestedData: false,
                sourceArrayName: key,
            });
        }
    }

    return results;
}

/**
 * STRUCTURAL: Check if a value is a typed grouping object (Pattern B).
 * Typed groupings have children with 'key' property.
 */
function isTypedGroupingObject(value: unknown): boolean {
    if (!isNonArrayObject(value)) return false;
    
    const objValue = value as Record<string, unknown>;
    const entries = Object.entries(objValue);
    if (entries.length === 0) return false;
    
    return entries.every(([, child]) => {
        if (!isNonArrayObject(child)) return false;
        return 'key' in (child as Record<string, unknown>);
    });
}

/**
 * Find sibling arrays linked by a common key.
 * Uses STRUCTURAL detection for Pattern B's typed grouping structure.
 */
export function findLinkedSiblingArrays(
    supportingData: Record<string, unknown>,
    excludeArrayName: string,
    linkingKey: string,
    linkingValue: string,
): DrillDownConfig[] {
    const results: DrillDownConfig[] = [];
    const normalizedValue = linkingValue.toLowerCase();

    for (const [arrayName, arrayValue] of Object.entries(supportingData)) {
        if (arrayName === excludeArrayName) continue;
        if (arrayName.startsWith('_')) continue;
        if (isTimeSeriesResponse(arrayValue)) continue;

        const data = normalizeToArray(arrayValue);
        if (data.length === 0) continue;

        const firstItem = data[0] as Record<string, unknown>;
        if (typeof firstItem !== 'object' || firstItem === null) continue;

        const pattern = detectPattern(data);
        let filteredItems: Record<string, unknown>[] = [];
        let actualLinkingKey: string | undefined;

        // Pattern B: Check inside typed grouping structure (structurally detected)
        if (pattern === ResponsePattern.AGGREGATE_WITH_GROUPINGS) {
            filteredItems = (data as Record<string, unknown>[]).filter(item => {
                const typedGrouping = findTypedGroupingObject(item);
                if (typedGrouping) {
                    for (const [groupType, groupData] of Object.entries(typedGrouping)) {
                        const mapping = GROUPING_TYPE_FIELD_MAP[groupType as GroupingTypeKey];
                        if (mapping && keysMatch(mapping.keyField, linkingKey)) {
                            if (String(groupData?.key || '').toLowerCase() === normalizedValue) {
                                actualLinkingKey = mapping.keyField;
                                return true;
                            }
                        }
                        // Also check unknown grouping types
                        if (keysMatch(groupType.toLowerCase() + '_id', linkingKey)) {
                            if (String(groupData?.key || '').toLowerCase() === normalizedValue) {
                                actualLinkingKey = groupType.toLowerCase() + '_id';
                                return true;
                            }
                        }
                    }
                }
                return false;
            });

            // Flatten matched items
            if (filteredItems.length > 0) {
                filteredItems = filteredItems.map(item => flattenPatternBEntity(item));
            }
        }

        // Standard: Check L1 keys
        if (filteredItems.length === 0) {
            const matchedKey = findMatchingKey(firstItem, linkingKey);
            if (matchedKey) {
                filteredItems = (data as Record<string, unknown>[]).filter(item => {
                    return String(item[matchedKey] || '').toLowerCase() === normalizedValue;
                });
                actualLinkingKey = matchedKey;
            }
        }

        if (filteredItems.length > 0) {
            results.push({
                title: formatLabel(arrayName),
                data: filteredItems,
                linkingKey: actualLinkingKey,
                hasNestedData: hasDeepNesting(filteredItems),
                sourceArrayName: arrayName,
            });
        }
    }

    return results;
}

/**
 * Check if an entity value has DEEPER drill-down data available in supportingData.
 * This is used to determine if a column should be clickable.
 * 
 * An entity is considered to have deeper drill-down if:
 * 1. There's a matching item in supportingData that contains nested arrays
 * 2. There's a matching item in supportingData that contains identifier-keyed objects
 * 3. Pattern B: The item has a data object with nested content
 * 
 * This is MORE restrictive than just checking if sibling arrays exist -
 * it ensures there's actually MORE information to show, not just leaf data.
 * 
 * @param supportingData - The rawSupportingData from alert
 * @param linkingKey - The key to match (e.g., 'warehouse_id')
 * @param linkingValue - The value to match (e.g., 'MK-BLR-DLV1')
 * @param excludeSourceArrayNames - Optional array of source names to exclude (already displayed configs)
 * @returns true if there's deeper drill-down data available
 */
export function hasDeeperDrillDownData(
    supportingData: Record<string, unknown>,
    linkingKey: string,
    linkingValue: string,
    excludeSourceArrayNames?: string[],
): boolean {
    for (const [arrayName, arrayValue] of Object.entries(supportingData)) {
        // Skip arrays that are already being displayed (or their nested sources)
        if (excludeSourceArrayNames) {
            const isExcluded = excludeSourceArrayNames.some(excludeName => 
                arrayName === excludeName || 
                excludeName.startsWith(arrayName + '_') ||
                arrayName.startsWith(excludeName.split('_')[0] + '_')
            );
            if (isExcluded) continue;
        }
        if (arrayName.startsWith('_')) continue;
        if (isTimeSeriesResponse(arrayValue)) continue;

        const data = normalizeToArray(arrayValue);
        if (data.length === 0) continue;

        const firstItem = data[0] as Record<string, unknown>;
        if (typeof firstItem !== 'object' || firstItem === null) continue;

        const pattern = detectPattern(data);
        
        // Find items that match the linking key/value
        const matchingItems = findMatchingItemsInArray(
            data as Record<string, unknown>[],
            linkingKey,
            linkingValue,
            pattern,
        );

        if (matchingItems.length === 0) continue;

        // Check if any matching item has nested data (not just leaf primitives)
        for (const item of matchingItems) {
            // Check for nested arrays
            for (const value of Object.values(item)) {
                if (Array.isArray(value) && value.length > 0 && typeof value[0] === 'object') {
                    return true;
                }
            }

            // Check for identifier-keyed objects (Pattern C groupings)
            for (const value of Object.values(item)) {
                if (isIdentifierKeyedObject(value)) {
                    return true;
                }
            }

            // Pattern B: Check if there's a data object with nested content
            if (pattern === ResponsePattern.AGGREGATE_WITH_GROUPINGS) {
                const dataObj = findDataObject(item);
                if (dataObj) {
                    for (const value of Object.values(dataObj)) {
                        if (Array.isArray(value) && value.length > 0) return true;
                        if (isNonArrayObject(value)) return true;
                    }
                }
            }
        }
    }

    return false;
}

/**
 * Check if any sibling arrays have a matching linking key.
 * Uses STRUCTURAL detection for Pattern B's typed grouping structure.
 */
export function hasSiblingArraysWithKey(
    supportingData: Record<string, unknown>,
    currentArrayName: string,
    linkingKey: string,
): boolean {
    for (const [arrayName, arrayValue] of Object.entries(supportingData)) {
        if (arrayName === currentArrayName) continue;
        if (arrayName.startsWith('_')) continue;
        if (isTimeSeriesResponse(arrayValue)) continue;

        const data = normalizeToArray(arrayValue);
        if (data.length === 0) continue;

        const firstItem = data[0] as Record<string, unknown>;
        if (typeof firstItem !== 'object' || firstItem === null) continue;

        // Check L1 keys
        if (findMatchingKey(firstItem, linkingKey)) {
            return true;
        }

        // Pattern B: Check inside typed grouping structure (structurally detected)
        const pattern = detectPattern(data);
        if (pattern === ResponsePattern.AGGREGATE_WITH_GROUPINGS) {
            const typedGrouping = findTypedGroupingObject(firstItem);
            if (typedGrouping) {
                for (const [groupType] of Object.entries(typedGrouping)) {
                    const mapping = GROUPING_TYPE_FIELD_MAP[groupType as GroupingTypeKey];
                    if (mapping && keysMatch(mapping.keyField, linkingKey)) {
                        return true;
                    }
                    // Also check unknown grouping types
                    if (keysMatch(groupType.toLowerCase() + '_id', linkingKey)) {
                        return true;
                    }
                }
            }
        }
    }
    return false;
}

// =============================================================================
// HELPER FUNCTIONS (exported for AlertDetailLayout)
// =============================================================================

/**
 * Detect response pattern from data structure using STRUCTURAL analysis.
 * Does NOT rely on specific key names - analyzes the shape of the data.
 * 
 * Detection Logic:
 * - Pattern B: Contains an object where ALL children have { key, name? } structure (typed grouping)
 * - Pattern C: Contains an object where children are data-only (no 'key' prop), keyed by identifiers
 * - Pattern A: Everything else (flat list, possibly with nested arrays)
 * - Pattern D: Single object (detected before calling this function)
 * - Pattern E: Scalar value (detected before calling this function)
 */
export function detectPattern(data: unknown[]): ResponsePattern {
    if (!Array.isArray(data) || data.length === 0) return ResponsePattern.PAGINATED_LIST;

    const first = data[0] as Record<string, unknown>;
    if (typeof first !== 'object' || first === null) return ResponsePattern.PAGINATED_LIST;

    // Check for Pattern B structure: typed grouping with { key, name? } children
    if (hasTypedGroupingStructure(first)) {
        return ResponsePattern.AGGREGATE_WITH_GROUPINGS;
    }

    // Check for Pattern C structure: identifier-keyed data objects + L1 primitives
    if (hasIdentifierKeyedGroupings(first)) {
        return ResponsePattern.ENTITY_WITH_NESTED_GROUPINGS;
    }

    return ResponsePattern.PAGINATED_LIST;
}

/**
 * STRUCTURAL DETECTION: Pattern B (AGGREGATE_WITH_GROUPINGS)
 * 
 * Characteristics:
 * - Contains an object property whose children ALL have a 'key' property
 * - This indicates typed grouping like: { PRODUCT: { key: 'SKU', name: 'Name' }, DATE: { key: '2024-01' } }
 * - Typically paired with a sibling 'data' object containing aggregate values
 * 
 * Example structures that match:
 * - { grouping: { PRODUCT: { key: 'x' } }, data: { qty: 5 } }
 * - { dimensions: { WAREHOUSE: { key: 'w1', name: 'Warehouse 1' } }, metrics: { count: 10 } }
 */
function hasTypedGroupingStructure(item: Record<string, unknown>): boolean {
    for (const value of Object.values(item)) {
        if (!isNonArrayObject(value)) continue;
        
        const objValue = value as Record<string, unknown>;
        const entries = Object.entries(objValue);
        
        // Need at least one child to check
        if (entries.length === 0) continue;
        
        // Check if ALL children have 'key' property (typed grouping pattern)
        const allChildrenHaveKeyProperty = entries.every(([, child]) => {
            if (!isNonArrayObject(child)) return false;
            return 'key' in (child as Record<string, unknown>);
        });
        
        if (allChildrenHaveKeyProperty) {
            return true;
        }
    }
    return false;
}

/**
 * STRUCTURAL DETECTION: Pattern C (ENTITY_WITH_NESTED_GROUPINGS)
 * 
 * Characteristics:
 * - Has L1 primitive properties (like product_id, quantity)
 * - Contains an object property keyed by identifiers (not types)
 * - Child objects do NOT have 'key' property (they ARE the data)
 * 
 * Example structures that match:
 * - { product_id: 'SKU', groupings: { 'BLR1': { qty: 5 }, 'DEL2': { qty: 3 } } }
 * - { sku: 'X', warehouses: { 'W001': { stock: 10 }, 'W002': { stock: 5 } } }
 * 
 * How we distinguish identifier keys from type keys:
 * - Type keys: ALL UPPERCASE like PRODUCT, DATE, CHANNEL
 * - Identifier keys: contain numbers OR are not all-uppercase
 */
function hasIdentifierKeyedGroupings(item: Record<string, unknown>): boolean {
    // Must have at least one L1 primitive (characteristic of entity-centric structure)
    const hasL1Primitives = Object.values(item).some(v => 
        v === null || typeof v !== 'object'
    );
    if (!hasL1Primitives) return false;
    
    for (const value of Object.values(item)) {
        if (!isNonArrayObject(value)) continue;
        
        const objValue = value as Record<string, unknown>;
        const entries = Object.entries(objValue);
        
        // Need at least one child to check
        if (entries.length === 0) continue;
        
        // Check if children are data objects (NO 'key' property - they ARE data, not typed groupings)
        const allChildrenAreDataObjects = entries.every(([, child]) => {
            if (!isNonArrayObject(child)) return false;
            return !('key' in (child as Record<string, unknown>));
        });
        
        if (!allChildrenAreDataObjects) continue;
        
        // Check if keys look like identifiers (contain numbers or are not ALL_UPPERCASE)
        const keysAreIdentifiers = entries.some(([k]) => {
            // Contains a digit → identifier (like BLR1, W001)
            if (/\d/.test(k)) return true;
            // Not all uppercase → identifier (like warehouse_blr, not PRODUCT)
            if (k !== k.toUpperCase()) return true;
            return false;
        });
        
        if (keysAreIdentifiers) {
            return true;
        }
    }
    return false;
}

/**
 * Helper: Check if value is a non-null, non-array object.
 */
function isNonArrayObject(value: unknown): boolean {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/**
 * Format a key into a human-readable label.
 */
export function formatLabel(key: string): string {
    return key
        .replace(/_/g, ' ')
        .replace(/([A-Z])/g, ' $1')
        .split(' ')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(' ')
        .trim();
}

/**
 * Normalize key for comparison (handles product_id vs productId).
 */
export function normalizeKey(key: string): string {
    return key.toLowerCase().replace(/_/g, '');
}

/**
 * Check if two keys are equivalent.
 */
export function keysMatch(key1: string, key2: string): boolean {
    return normalizeKey(key1) === normalizeKey(key2);
}

/**
 * Find matching key in an object (handles product_id vs productId).
 */
export function findMatchingKey(obj: Record<string, unknown>, targetKey: string): string | null {
    for (const key of Object.keys(obj)) {
        if (keysMatch(key, targetKey)) {
            return key;
        }
    }
    return null;
}

// =============================================================================
// PRIVATE HELPER FUNCTIONS
// =============================================================================

function findPrimaryKey(entity: Record<string, unknown>): string | undefined {
    const priorityKeys = ['product_id', 'productId', 'purchaseOrderId', 'salesId', 'warehouseId', 'warehouse_id', 'channelId', 'vendorId', 'id'];
    for (const key of priorityKeys) {
        if (key in entity) return key;
    }
    return undefined;
}

function findLinkingKeyInData(item: Record<string, unknown>): string | undefined {
    for (const linkKey of LINKING_KEYS) {
        if (linkKey in item) return linkKey;
    }
    return undefined;
}

function normalizeToArray(data: unknown): unknown[] {
    if (Array.isArray(data)) return data;
    if (data !== null && typeof data === 'object') {
        const objData = data as Record<string, unknown>;
        
        // Handle wrapped data - check common wrapper keys: 'data', 'value', 'items', 'results'
        // These are common response wrapper patterns from various services
        const wrapperKeys = ['data', 'value', 'items', 'results'];
        for (const wrapperKey of wrapperKeys) {
            if (wrapperKey in objData && Array.isArray(objData[wrapperKey])) {
                return objData[wrapperKey] as unknown[];
            }
        }
        
        // If it's a single object with no recognized wrapper, treat as single-item array
        return [data];
    }
    return [];
}

function expandGroupingsObject(groupings: Record<string, Record<string, unknown>>): Record<string, unknown>[] {
    const results: Record<string, unknown>[] = [];
    
    for (const [key, value] of Object.entries(groupings)) {
        // Skip non-object values (metadata)
        if (!isNonArrayObject(value)) continue;
        
        // Determine the best identifier field name based on existing fields in value
        // or use a generic 'id' field
        const hasWarehouseId = 'warehouse_id' in value || 'warehouseId' in value;
        const hasId = 'id' in value;
        
        const expanded: Record<string, unknown> = {};
        
        // Add the key as an identifier
        if (!hasWarehouseId && !hasId) {
            // Use 'id' as a generic identifier, also add 'name' for display
            expanded.id = key;
            expanded.name = key;
        }
        
        // Add warehouse_id if the key looks like a warehouse identifier
        if (key.includes('WH') || key.includes('wh') || key.includes('-') || key.includes('MK')) {
            expanded.warehouse_id = key;
            if (!expanded.name) expanded.name = key;
        }
        
        // Spread the value's properties
        Object.assign(expanded, value);
        
        results.push(expanded);
    }
    
    return results;
}

function hasDeepNesting(data: unknown[]): boolean {
    if (data.length === 0) return false;
    const first = data[0] as Record<string, unknown>;
    if (typeof first !== 'object' || first === null) return false;

    for (const value of Object.values(first)) {
        if (Array.isArray(value) && value.length > 0 && typeof value[0] === 'object') return true;
        if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
            const entries = Object.entries(value);
            if (entries.length > 0 && typeof entries[0][1] === 'object') return true;
        }
    }
    return false;
}
